import { TIER_CAPS, type SubscriptionTier } from '@/modules/workspaces/workspace-features';

/** Rungs that appear on a marketing surface. */
export type MarketingTierId = 'free' | 'launch' | 'grow' | 'scale' | 'enterprise';

/**
 * The rungs a customer can start on without talking to anyone. Enterprise is
 * negotiated, so it is absent.
 */
export const SELF_SERVE_TIER_IDS = ['free', 'launch', 'grow', 'scale'] as const;
export type SelfServeTierId = typeof SELF_SERVE_TIER_IDS[number];

export type MarketingTier = {
  id: MarketingTierId;
  name: string;
  tagline: string;
  /**
   * `annualMonthly` is the per-month figure a buyer on annual billing pays, and
   * `annualTotal` is what is charged once. Enterprise is negotiated, so it
   * carries no figures at all rather than an invented one.
   */
  price: {
    monthly: number;
    annualMonthly: number;
    annualTotal: number;
  } | null;
  annualSavingsPct?: number;
  cta: string;
  highlighted: boolean;
  badge?: string;
  /** Sits under the CTA. Launch uses it to name the trial lift (#348). */
  ctaNote?: string;
};

export const MARKETING_TIERS: MarketingTier[] = [
  {
    id: 'free',
    name: 'Free',
    tagline: 'For solo operators getting started.',
    price: { monthly: 0, annualMonthly: 0, annualTotal: 0 },
    cta: 'Get started free',
    ctaNote: 'No credit card required',
    highlighted: false,
  },
  {
    id: 'launch',
    name: 'Launch',
    tagline: 'For a leadership team getting on the same page.',
    price: { monthly: 99, annualMonthly: 79, annualTotal: 948 },
    annualSavingsPct: 20,
    cta: 'Start free trial',
    ctaNote: '14-day free trial of Grow features',
    highlighted: false,
  },
  {
    id: 'grow',
    name: 'Grow',
    tagline: 'For a company aligning strategy top to bottom.',
    price: { monthly: 249, annualMonthly: 199, annualTotal: 2388 },
    annualSavingsPct: 20,
    cta: 'Start free trial',
    ctaNote: '14-day free trial',
    highlighted: true,
    badge: 'Most popular',
  },
  {
    id: 'scale',
    name: 'Scale',
    tagline: 'For running a company its leaders can\'t see day to day.',
    price: { monthly: 599, annualMonthly: 479, annualTotal: 5748 },
    annualSavingsPct: 20,
    cta: 'Start free trial',
    ctaNote: '14-day free trial',
    highlighted: false,
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    tagline: 'For organizations with procurement and security requirements.',
    price: null,
    cta: 'Contact sales',
    highlighted: false,
  },
];

/** The four rungs that carry a price and sit in the comparison grid. */
export const PRICED_TIERS = MARKETING_TIERS.filter((t) => t.price !== null);

export const ENTERPRISE_TIER = MARKETING_TIERS.find((t) => t.id === 'enterprise')!;

/**
 * What one row of the comparison grid says about one rung.
 *
 * `true` is a plain inclusion of the row's own label, `false` an absence the
 * visitor is meant to feel, and a string is a replacement line. The cards are
 * too narrow to carry a label and a right-aligned qualifier, so a rung that
 * differs by degree says so in one piece of prose — "Up to 30 users"
 * rather than "Users" set against a separate "Up to 30".
 */
export type MarketingRowValue = boolean | string;

export type MarketingFeatureRow = {
  label: string;
  values: Record<MarketingTierId, MarketingRowValue>;
};

function capRow(
  label: string,
  key: 'seats' | 'teams',
  render: (n: number) => string,
  enterprise: string,
): MarketingFeatureRow {
  const of = (id: MarketingTierId): string => {
    const n = TIER_CAPS[id as SubscriptionTier][key];
    return n === null ? `Unlimited ${label}` : render(n);
  };
  return {
    label,
    values: {
      free: of('free'),
      launch: of('launch'),
      grow: of('grow'),
      scale: of('scale'),
      enterprise,
    },
  };
}

/**
 * The landing page's comparison grid.
 *
 * Hand-written for a cold visitor, and deliberately not derived from
 * `TIER_UPGRADE_FEATURES`. That list addresses a customer who already owns the
 * rung below and knows the vocabulary; this one addresses someone who has never
 * seen the product. The two are free to drift, and should.
 *
 * Every rung answers every row, so the four columns read across. Seat and team
 * counts still come from `TIER_CAPS`, because those are enforced in the database
 * and a number advertised here that the database refuses is a defect, not copy.
 */
export const MARKETING_FEATURE_ROWS: MarketingFeatureRow[] = [
  capRow(
    'users',
    'seats',
    (n) => (n === 1 ? 'Just you' : `Up to ${n} users`),
    '75+ users',
  ),
  {
    label: 'Rocks, metrics, tasks & issues',
    values: { free: true, launch: true, grow: true, scale: true, enterprise: true },
  },
  {
    label: 'Annual & quarterly planning',
    values: { free: true, launch: true, grow: true, scale: true, enterprise: true },
  },
  {
    label: 'Process documentation',
    values: { free: true, launch: true, grow: true, scale: true, enterprise: true },
  },
  {
    // A fork is a whole second chart, so the honest unit is a count rather than
    // a verb. No fork limit is enforced yet; the gate is its own issue.
    label: 'Org charts',
    values: {
      free: '1 org chart',
      launch: 'Future-state org charts',
      grow: 'Future-state org charts',
      scale: 'Future-state org charts',
      enterprise: 'Future-state org charts',
    },
  },
  {
    // The marketing surface took the name "Teams" first (#349), while the
    // in-app and database noun was still Spaces. #367 has since moved that one
    // too, so the two now agree and this row needs no separate vocabulary.
    // The label only renders where the value is a bare true/false, which here is
    // Free alone. An omission gets a short line, not an explanation.
    label: 'Separate teams',
    values: {
      free: false,
      launch: '2 teams',
      grow: '8 teams',
      scale: 'Unlimited teams',
      enterprise: 'Unlimited teams',
    },
  },
  {
    label: 'Team meetings & scheduling',
    values: { free: false, launch: true, grow: true, scale: true, enterprise: true },
  },
  {
    label: 'Meeting prep & recap emails',
    values: { free: false, launch: true, grow: true, scale: true, enterprise: true },
  },
  {
    label: 'AI insights',
    values: {
      free: false,
      launch: 'Core AI insights',
      grow: 'All AI insights',
      scale: 'All AI insights',
      enterprise: 'All AI insights',
    },
  },
  {
    label: 'Integrations',
    values: {
      free: false,
      launch: 'Core integrations',
      grow: 'All integrations',
      scale: 'All integrations',
      enterprise: 'All integrations',
    },
  },
  {
    label: 'Business health diagnostics',
    values: { free: false, launch: false, grow: true, scale: true, enterprise: true },
  },
  {
    label: 'Workplace personality assessments',
    values: { free: false, launch: false, grow: true, scale: true, enterprise: true },
  },
  {
    label: 'Custom terminology & meeting agendas',
    values: { free: false, launch: false, grow: true, scale: true, enterprise: true },
  },
  {
    label: 'Role fit analysis',
    values: { free: false, launch: false, grow: false, scale: true, enterprise: true },
  },
  {
    label: 'Audit log',
    values: { free: false, launch: false, grow: false, scale: true, enterprise: true },
  },
  {
    label: 'Webhooks, API & MCP',
    values: { free: false, launch: false, grow: false, scale: true, enterprise: true },
  },
];

/**
 * What the Enterprise band sells on. It sits below the grid rather than in it,
 * so it names only what nothing else offers.
 */
export const ENTERPRISE_HIGHLIGHTS: string[] = [
  'Unlimited seats and teams',
  'SSO & SCIM provisioning',
  'Contractual retention, deletion, and audit terms',
  'MSA, DPA, and a named subprocessor list',
  'Invoicing with a PO number and net-30 terms',
  'Guided migration of your existing data',
  'Training for your leadership team on running the operating system',
];

/** Seats and teams a rung admits. `null` is uncapped, rendered as "Unlimited". */
export function capsFor(id: MarketingTierId): { seats: number | null; teams: number | null } {
  return TIER_CAPS[id as SubscriptionTier];
}
